import React, { useCallback, useEffect, useRef, useState } from 'react';
import DeviceDataService from '../../services/DeviceService';
import Badge from '@material-ui/core/Badge';
import MaterialTable from 'material-table';
import { API_URL } from '../../utils/setApiUrl.js';
import axios from 'axios';
import { Snackbar, TextField, Autocomplete } from '@mui/material';
import Alert from '@material-ui/lab/Alert';
import LocationService from '../../services/LocationService';
import useCheckPermission from '../../utils/permissions/useCheckPermissions';
import { useTranslation } from 'react-i18next';

const TABLE_OUTER_CHROME_FALLBACK = 140;

const DevicesTable = ({ className }) => {
    const { t } = useTranslation();
    const tableContainerRef = useRef(null);
    const [bodyHeight, setBodyHeight] = useState(
        () => Math.max(200, window.innerHeight - 128 - TABLE_OUTER_CHROME_FALLBACK - 8)
    );
    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });

    const isSuperUser = useCheckPermission('super');
    const level = localStorage.getItem('level');
    const canViewDeviceNetworkInfo =
        level === 'super' || level === 'admin';

    const client = axios.create({
        baseURL: API_URL,
    });

    const [devices, setDevice] = useState([]);
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        // Call the function on component mount
        retrieveDevices();
        // Add locations fetch
        fetchLocations();

        // Set up an interval to call the function every 60 seconds (60000 milliseconds)
        const interval = setInterval(retrieveDevices, 60000);

        // Clear the interval on component unmount
        return () => clearInterval(interval);
    }, []);

    const updateBodyHeight = useCallback(() => {
        const container = tableContainerRef.current;
        const card = container && container.parentElement;
        if (!card) {
            return;
        }

        const cardHeight = card.clientHeight;
        const tableRoot = container.querySelector('.MuiPaper-root');

        if (!tableRoot) {
            setBodyHeight(Math.max(200, cardHeight - TABLE_OUTER_CHROME_FALLBACK - 8));
            return;
        }

        // maxBodyHeight is applied to the scroll wrapper that contains thead + tbody.
        // Only subtract fixed chrome outside that wrapper (toolbar + pagination footer).
        const toolbar = tableRoot.querySelector('.MuiToolbar-root');
        const footer = tableRoot.querySelector('tfoot');

        let outerChrome = 0;
        if (toolbar) {
            outerChrome += toolbar.offsetHeight;
        }
        if (footer) {
            outerChrome += footer.offsetHeight;
        }
        if (!outerChrome) {
            outerChrome = TABLE_OUTER_CHROME_FALLBACK;
        }

        const nextHeight = Math.max(200, cardHeight - outerChrome - 8);
        setBodyHeight(currentHeight =>
            currentHeight === nextHeight ? currentHeight : nextHeight
        );
    }, []);

    useEffect(() => {
        updateBodyHeight();
        window.addEventListener('resize', updateBodyHeight);

        const timeoutId = window.setTimeout(updateBodyHeight, 150);

        return () => {
            window.removeEventListener('resize', updateBodyHeight);
            window.clearTimeout(timeoutId);
        };
    }, [updateBodyHeight, devices.length]);

    const retrieveDevices = async () => {
        const locData = JSON.parse(localStorage.getItem('loc'));

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await DeviceDataService.getAll(joinedValues);
            } else if (locData.id === 'all') {
                response = await DeviceDataService.adminAll();
            }

            if (response) {
                setDevice(response.data);
                console.log(response.data);
            }
        } catch (e) {
            console.log(e);
        }
    };

    // Add function to fetch locations
    const fetchLocations = async () => {
        try {
            const response = await LocationService.getAll();
            setLocations(response.data);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    // Add function to handle location update
    const handleLocationUpdate = async (newData, oldData) => {
        try {
            console.log('Updating device:', newData); // Debug log
            console.log('New location:', newData.DeviceLocation); // Debug log
            await DeviceDataService.update(newData.mac, {
                location: parseInt(newData.DeviceLocation.number),
            });
            handleShowSnackbar('Локацията е успешно променена!', 'success');
            retrieveDevices(); // Refresh the table
        } catch (error) {
            console.error('Update error:', error);
            handleShowSnackbar('Грешка при промяна на локацията!', 'error');
            throw error;
        }
    };

    // Snackbars Start
    const [snackbars, setSnackbars] = useState([]);

    const handleCloseSnackbar = id => {
        setSnackbars(prevSnackbars =>
            prevSnackbars.filter(snackbar => snackbar.id !== id)
        );
    };

    const handleShowSnackbar = (message, severity) => {
        const id = new Date().getTime(); // Unique ID for each snackbar
        const newSnackbar = {
            id,
            message,
            severity,
            position: snackbars.length * 55,
        };

        setSnackbars(prevSnackbars => [...prevSnackbars, newSnackbar]);
    };
    //Snackbars End

    // * Functionality for restarting devices START
    const restartDevice = async rowData => {
        const { name, mac } = rowData;

        try {
            const res = await client.post('/restartDevice', { mac });

            if (res.status === 200) {
                handleShowSnackbar(
                    t('devices.devicesTable.restartSuccess'),
                    'success'
                );
            }
        } catch (err) {
            handleShowSnackbar(t('devices.devicesTable.restartError'), 'error');
        }
    };
    // * Functionality for restarting devices END

    // * Functionality for checking devices alive START
    const checkDevicesAlive = async () => {
        try {
            const res = await client.post('/whoisalive');

            if (res.status === 200) {
                handleShowSnackbar(
                    t('devices.devicesTable.checkAliveSuccess') ||
                        'Online devices check completed successfully',
                    'success'
                );
            }
        } catch (err) {
            handleShowSnackbar(
                t('devices.devicesTable.checkAliveError') ||
                    'Error checking online devices',
                'error'
            );
        } finally {
            // Always refresh the devices list, even if there was an error
            retrieveDevices();
        }
    };
    // * Functionality for checking devices alive END

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const handleNameUpdate = async (newData, oldData) => {
        try {
            console.log('Updating device name:', newData);
            await DeviceDataService.update(newData.mac, {
                name: newData.name,
            });
            handleShowSnackbar('Името е успешно променено!', 'success');
            retrieveDevices();
        } catch (error) {
            console.error('Name update error:', error);
            handleShowSnackbar('Грешка при промяна на името!', 'error');
            throw error;
        }
    };

    const handleUpdate = async (newData, oldData) => {
        try {
            const updates = {};

            // Check if location was changed
            const newLocationNumber =
                newData.DeviceLocation && newData.DeviceLocation.number;
            const oldLocationNumber =
                oldData.DeviceLocation && oldData.DeviceLocation.number;
            if (newLocationNumber !== oldLocationNumber) {
                updates.location = parseInt(newData.DeviceLocation.number);
            }

            // Check if name was changed
            if (newData.name !== oldData.name) {
                updates.name = newData.name;
            }

            // Check if MAC was changed (for super users)
            if (isSuperUser && newData.mac !== oldData.mac) {
                updates.mac = newData.mac;
            }

            if (Object.keys(updates).length > 0) {
                // If MAC is being updated, use the old MAC as identifier
                const identifierMac = updates.mac ? oldData.mac : newData.mac;
                await DeviceDataService.update(identifierMac, updates);
                handleShowSnackbar(
                    t('devices.devicesTable.checkAliveSuccess'),
                    'success'
                );
                retrieveDevices();
            }
        } catch (error) {
            console.error('Update error:', error);
            handleShowSnackbar(
                t('devices.devicesTable.checkAliveError'),
                'error'
            );
            throw error;
        }
    };

    const columns = [
        {
            title: t('devices.devicesTable.device'),
            field: 'name',
            editComponent: props => (
                <TextField
                    value={props.value || ''}
                    onChange={e => props.onChange(e.target.value)}
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiInput-underline:after': {
                            borderBottomColor: '#003d4d',
                        },
                    }}
                />
            ),
            render: rowData => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                        color={rowData.online ? 'secondary' : 'error'}
                        variant="dot"
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    />
                    <span style={{ marginLeft: '15px' }}>{rowData.name}</span>
                </div>
            ),
        },
        {
            title: t('devices.devicesTable.location'),
            field: 'DeviceLocation.name',
            editComponent: props => (
                <Autocomplete
                    options={locations}
                    getOptionLabel={option => option.name || ''}
                    value={
                        locations.find(
                            loc =>
                                loc.number ===
                                parseInt(
                                    props.rowData.DeviceLocation &&
                                        props.rowData.DeviceLocation.number
                                )
                        ) || null
                    }
                    onChange={(event, newValue) => {
                        console.log('Selected location:', newValue);
                        const updatedLocation = newValue
                            ? {
                                  id: newValue.id,
                                  number: newValue.number,
                                  name: newValue.name,
                              }
                            : null;
                        props.onChange(updatedLocation);
                        props.onRowDataChange({
                            ...props.rowData,
                            DeviceLocation: updatedLocation,
                        });
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder="Select location"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#003d4d',
                                    },
                                },
                            }}
                        />
                    )}
                    sx={{
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: '#003d4d',
                            },
                        },
                    }}
                    isOptionEqualToValue={(option, value) =>
                        option.number === value.number
                    }
                    blurOnSelect
                    openOnFocus
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                />
            ),
        },
        ...(canViewDeviceNetworkInfo
            ? [
                  {
                      title: t('devices.devicesTable.ipAddress'),
                      field: 'ip_address',
                      editable: 'never',
                  },
                  {
                      title: t('devices.devicesTable.mac'),
                      field: 'mac',
                      editable: isSuperUser ? undefined : 'never',
                      editComponent: isSuperUser
                          ? props => (
                                <TextField
                                    value={props.value || ''}
                                    onChange={e =>
                                        props.onChange(e.target.value)
                                    }
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiInput-underline:after': {
                                            borderBottomColor: '#003d4d',
                                        },
                                    }}
                                />
                            )
                          : undefined,
                  },
              ]
            : []),
    ];
    return (
        <div ref={tableContainerRef} className={className}>
            {snackbars
                .slice()
                .reverse()
                .map((snackbar, index) => (
                    <Snackbar
                        key={snackbar.id}
                        open={true}
                        autoHideDuration={5000}
                        onClose={() => handleCloseSnackbar(snackbar.id)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        style={{ bottom: `${10 + index * 55}px` }}
                    >
                        <Alert
                            elevation={6}
                            variant="filled"
                            severity={snackbar.severity}
                            onClose={() => handleCloseSnackbar(snackbar.id)}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                ))}
            <MaterialTable
                title={t('devices.devicesTable.title')}
                data={devices}
                columns={columns}
                style={{
                    borderRadius: '0',
                    boxShadow: 'none',
                    width: '100%',
                }}
                actions={[
                    {
                        icon: 'refresh',
                        tooltip: t('devices.devicesTable.refresh'),
                        isFreeAction: true,
                        onClick: retrieveDevices,
                    },
                    {
                        icon: 'network_check',
                        tooltip:
                            t('devices.devicesTable.checkAlive') ||
                            'Check online devices',
                        isFreeAction: true,
                        onClick: checkDevicesAlive,
                    },
                    {
                        icon: 'cached',
                        tooltip: t('devices.devicesTable.restart'),
                        onClick: (event, rowData) => restartDevice(rowData),
                    },
                ]}
                options={{
                    loadingType: 'linear',
                    pageSize: 100,
                    maxBodyHeight: bodyHeight,
                    showTitle: true,
                    sorting: true,
                    actionsColumnIndex: -1,
                    exportButton: { csv: true, pdf: false },
                    filtering: true,
                    pageSizeOptions: [20, 50, 100, 500, 1000],
                    whiteSpace: 'nowrap',
                    rowStyle: {
                        overflowWrap: 'break-word',
                    },
                    search: false,
                    columnResizable: false,
                }}
                editable={{
                    onRowUpdate: (newData, oldData) =>
                        new Promise((resolve, reject) => {
                            handleUpdate(newData, oldData)
                                .then(() => resolve())
                                .catch(() => reject());
                        }),
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows:
                            '{from}-{to} ' +
                            t('reports.common.of') +
                            ' {count}',
                        labelRowsPerPage: t('reports.common.rowsPerPage'),
                        labelRowsSelect: t('reports.common.rowsSelect'),
                    },
                    header: {
                        actions: t('reports.common.actions'),
                    },
                    body: {
                        emptyDataSourceMessage: t(
                            'reports.common.emptyDataSourceMessage'
                        ),
                        editTooltip: t('reports.common.edit'),
                        deleteTooltip: t('reports.common.delete'),
                        filterRow: {
                            filterTooltip: t('reports.common.filter'),
                        },
                        editRow: {
                            deleteText: t('devices.common.deleteConfirmation'),
                            cancelTooltip: t('devices.common.cancel'),
                            saveTooltip: t('devices.common.save'),
                        },
                    },
                    toolbar: {
                        exportTitle: t('reports.common.export'),
                        exportName: t('reports.common.exportCSV'),
                        searchTooltip: t('reports.common.search'),
                        searchPlaceholder: t(
                            'reports.common.searchPlaceholder'
                        ),
                    },
                }}
            />
        </div>
    );
};

export default DevicesTable;
